import { utilities } from 'utils/utilities';
import { EnrollmentStatus } from './EnrollmentStatus';

export interface EnrollmentDto {
  id: string;
  enrollmentType: string;
  dtoType: string; // 'Models.Api.Dtos.EnrollmentDto';
  nodeInstanceId?: string;
  requiredSubmissionType?: string;
  status?: string;
  satisfiedAt?: string;
}

// Enrollment.ts — make this an abstract base so concrete enrollments (like FormEnrollment)
// must implement their own shape and toDto().
export abstract class Enrollment {
  id: string;
  abstract readonly type: string;

  nodeInstanceId: string = utilities.DEFAULT_GUID;
  requiredSubmissionType: string = '';
  status: EnrollmentStatus = EnrollmentStatus.Open;
  satisfiedAt?: string;

  constructor(id?: string) {
    this.id = id ?? utilities.DEFAULT_GUID;
  }

  abstract toDto(): EnrollmentDto;

  /** Returns true if this enrollment's requiredSubmissionType matches the given type (case-insensitive). */
  matchesSubmissionType(type: string): boolean {
    return this.requiredSubmissionType.toLowerCase() === type.toLowerCase();
  }

  /** @deprecated Use matchesSubmissionType instead. */
  matchesUserSubmissionType(type: string): boolean {
    return this.matchesSubmissionType(type);
  }

  /**
   * Create an Enrollment instance from either an Enrollment instance or a plain DTO.
   * Dispatches to concrete subclass factories when possible.
   */
  private static registry: Map<string, (dto?: any) => Enrollment> = new Map();

  static register(type: string, factory: (dto?: any) => Enrollment) {
    Enrollment.registry.set(type, factory);
  }

  static fromDto(dto?: Partial<EnrollmentDto> | Enrollment | any): Enrollment {
    console.log("Enrollment DTO " + JSON.stringify(dto));
    if (!dto) {
      return new GenericEnrollment(utilities.DEFAULT_GUID, '');
    }

    if (dto instanceof Enrollment) {
      console.log("Returning existing Enrollment instance");
      return dto;
    }

    const type = dto.type ?? dto.enrollmentType ?? '';
    const factory = Enrollment.registry.get(type);
    let enrollment: Enrollment | null = null;

    if (factory) {
      enrollment = factory(dto);
    } else {
      console.log("No factory found for type " + type);

      // If no factory is registered yet, try a lazy require for known built-in types.
      // This avoids adding a top-level import (which can cause circular init problems)
      try {
        switch (type) {
          case 'FormEnrollment':
          case 'Models.Enrollments.FormEnrollment': {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const mod = require('./FormEnrollment') as typeof import('./FormEnrollment');
            if (mod && typeof mod.FormEnrollment?.fromDto === 'function') {
              enrollment = mod.FormEnrollment.fromDto(dto);
            }
            break;
          }
          case 'ReviewEnrollment': {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const mod = require('./ReviewEnrollment') as typeof import('./ReviewEnrollment');
            if (mod && typeof mod.ReviewEnrollment?.fromDto === 'function') {
              enrollment = mod.ReviewEnrollment.fromDto(dto);
            }
            break;
          }
          case 'EventEnrollment': {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const mod = require('./EventEnrollment') as typeof import('./EventEnrollment');
            if (mod && typeof mod.EventEnrollment?.fromDto === 'function') {
              enrollment = mod.EventEnrollment.fromDto(dto);
            }
            break;
          }
          case 'ConsultationEnrollment': {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const mod = require('./ConsultationEnrollment') as typeof import('./ConsultationEnrollment');
            if (mod && typeof mod.ConsultationEnrollment?.fromDto === 'function') {
              enrollment = mod.ConsultationEnrollment.fromDto(dto);
            }
            break;
          }
          case 'AppointmentEnrollment': {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const mod = require('./AppointmentEnrollment') as typeof import('./AppointmentEnrollment');
            if (mod && typeof mod.AppointmentEnrollment?.fromDto === 'function') {
              enrollment = mod.AppointmentEnrollment.fromDto(dto);
            }
            break;
          }
          case 'Models.Enrollments.ProjectEstimationEnrollment': {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const mod = require('./ProjectEstimationEnrollment') as typeof import('./ProjectEstimationEnrollment');
            if (mod && typeof mod.ProjectEstimationEnrollment?.fromDto === 'function') {
              enrollment = mod.ProjectEstimationEnrollment.fromDto(dto);
            }
            break;
          }
        }
      } catch (err) {
        // ignore and fall back to GenericEnrollment
      }
    }

    if (!enrollment) {
      // unknown type -> return a generic concrete implementation so callers can still call toDto()
      enrollment = new GenericEnrollment(dto.id ?? utilities.DEFAULT_GUID, type);
    }

    // Apply base lifecycle fields from DTO after dispatch
    enrollment.nodeInstanceId = dto.nodeInstanceId ?? utilities.DEFAULT_GUID;
    enrollment.requiredSubmissionType = dto.requiredSubmissionType ?? '';
    enrollment.status = (dto.status as EnrollmentStatus) ?? EnrollmentStatus.Open;
    enrollment.satisfiedAt = dto.satisfiedAt;

    return enrollment;
  }
}

class GenericEnrollment extends Enrollment {
  readonly type: string;

  constructor(id: string, type: string) {
    super(id);
    this.type = type;
  }

  toDto(): EnrollmentDto {
    return { id: this.id, enrollmentType: this.type, dtoType: 'Models.Api.Dtos.EnrollmentDto' };
  }
}
